import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { usePageTitle } from "./lib/usePageTitle";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import AcceptInvite from "./pages/AcceptInvite";
import Dashboard from "./pages/Dashboard";
import ConsentLink from "./pages/ConsentLink";
import Sessions from "./pages/Sessions";
import SessionRoom from "./pages/SessionRoom";
import SessionEditor from "./pages/SessionEditor";
import DeliveryStatus from "./pages/DeliveryStatus";
import Settings from "./pages/Settings";
import BillingSuccess from "./pages/BillingSuccess";
import PlatformAdmin from "./pages/PlatformAdmin";
import Reports from "./pages/Reports";
import PublicConsent from "./pages/PublicConsent";
import ClientRoom from "./pages/ClientRoom";
import GroupRoomJoin from "./pages/GroupRoomJoin";
import GroupRoomHost from "./pages/GroupRoomHost";
import ShareKit from "./pages/ShareKit";
import { ForgotPassword, ResetPassword, ResetAuthenticator } from "./pages/PasswordReset";
import Clients, { ClientRecord } from "./pages/Clients";
import ConsentStatus from "./pages/ConsentStatus";
import TextReminders from "./pages/TextReminders";
import PublicSign from "./pages/PublicSign";
import Landing, { LegalPage } from "./pages/Landing";
import Matching from "./pages/Matching";
import { NewDocument, DocumentWorkspace } from "./pages/Documents";
import Clinicals from "./pages/Clinicals";
import Dla20Home, { Dla20Assessment } from "./pages/Dla20";
import ReferralHome, { ReferralDetail, ReferredClientWorkers } from "./pages/Referrals";
import Runmila from "./pages/Runmila";
import { ComingSoon } from "./pages/ComingSoon";
import Authorizations from "./pages/Authorizations";
import MyBilling, { BatchDetail, BillerQueue } from "./pages/ServiceBilling";
import QA from "./pages/QA";
import IntakeBuilder from "./pages/IntakeBuilder";
import ReferralBuilder from "./pages/ReferralBuilder";
import Schedule from "./pages/Schedule";
import EncounterNote from "./pages/EncounterNote";
import PublicIntake from "./pages/PublicIntake";
import { ClinicalTemplates, LinksPage, AuditPage } from "./pages/AgencyTools";
import Billing from "./pages/Billing";
import ConsentBuilder from "./pages/ConsentBuilder";
import { Onboarding, Readiness } from "./pages/Onboarding";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

const ClientRecordRoute = () => { const { id } = useParams(); return <ClientRecord id={id} />; };

function Protected({ children, bare }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return bare ? children : <Layout>{children}</Layout>;
}

function LoginGate() {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return <Login />;
}

function PageTitle() { usePageTitle(); return null; }

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PageTitle />
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            {["/consent", "/intake", "/clinical", "/workflow", "/pricing", "/demo", "/security"].map((p) => <Route key={p} path={p} element={<Navigate to="/" replace />} />)}
            <Route path="/login" element={<LoginGate />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-authenticator" element={<ResetAuthenticator />} />
            <Route path="/:slug/consent" element={<PublicConsent />} />
            <Route path="/:slug/consentforservices" element={<PublicConsent />} />
            <Route path="/:companySlug/intake" element={<PublicIntake />} />
            <Route path="/:companySlug/pre-intake" element={<PublicIntake />} />
            <Route path="/:slug/share" element={<ShareKit />} />
            <Route path="/sign/:token" element={<PublicSign />} />
            <Route path="/g/:code" element={<GroupRoomJoin />} />
            <Route path="/:slug/:room" element={<ClientRoom />} />
            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/app/clients/:id" element={<Protected><ClientRecordRoute /></Protected>} />
            <Route path="/app/documents" element={<Protected><Clinicals /></Protected>} />
            <Route path="/app/clinicals" element={<Protected><Clinicals /></Protected>} />
            <Route path="/app/documents/new" element={<Protected><NewDocument /></Protected>} />
            <Route path="/app/documents/:id" element={<Protected><DocumentWorkspace /></Protected>} />
            <Route path="/app/dla20" element={<Protected><Dla20Home /></Protected>} />
            <Route path="/app/dla20/:id" element={<Protected><Dla20Assessment /></Protected>} />
            <Route path="/app/referrals" element={<Protected><ReferralHome /></Protected>} />
            <Route path="/app/referrals/workers" element={<Protected><ReferredClientWorkers /></Protected>} />
            <Route path="/app/referrals/:id" element={<Protected><ReferralDetail /></Protected>} />
            <Route path="/app/runmila" element={<Protected><Runmila /></Protected>} />
            <Route path="/app/authorizations" element={<Protected><Authorizations /></Protected>} />
            <Route path="/app/my-billing" element={<Protected><MyBilling /></Protected>} />
            <Route path="/app/my-billing/:id" element={<Protected><BatchDetail /></Protected>} />
            <Route path="/app/biller" element={<Protected><BillerQueue /></Protected>} />
            <Route path="/app/biller/:id" element={<Protected><BatchDetail biller /></Protected>} />
            <Route path="/app/qa" element={<Protected><QA /></Protected>} />
            <Route path="/app/matching" element={<Protected><Matching /></Protected>} />
            <Route path="/app/consent-status" element={<Protected><ConsentStatus /></Protected>} />
            <Route path="/app/reminders" element={<Protected><TextReminders /></Protected>} />
            <Route path="/app/intake-builder" element={<Protected><IntakeBuilder /></Protected>} />
            <Route path="/app/referral-builder" element={<Protected><ReferralBuilder /></Protected>} />
            <Route path="/app/schedule" element={<Protected><Schedule /></Protected>} />
            <Route path="/app/schedule/:aid/note" element={<Protected><EncounterNote /></Protected>} />
            <Route path="/app/encounters/:eid" element={<Protected><EncounterNote /></Protected>} />
            <Route path="/app/consent-builder" element={<Protected><ConsentBuilder /></Protected>} />
            <Route path="/app/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/app/readiness" element={<Protected><Readiness /></Protected>} />
            <Route path="/app/templates" element={<Protected><ClinicalTemplates /></Protected>} />
            <Route path="/app/links" element={<Protected><LinksPage /></Protected>} />
            <Route path="/app/audit" element={<Protected><AuditPage /></Protected>} />
            <Route path="/app/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/app/consent-link" element={<Protected><ConsentLink /></Protected>} />
            <Route path="/app/sessions" element={<Protected><Sessions /></Protected>} />
            <Route path="/app/sessions/:id/room" element={<Protected bare><SessionRoom /></Protected>} />
            <Route path="/app/group-rooms/:id" element={<Protected><GroupRoomHost /></Protected>} />
            <Route path="/app/sessions/:id" element={<Protected><SessionEditor /></Protected>} />
            <Route path="/app/delivery/email" element={<Protected><DeliveryStatus /></Protected>} />
            <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/app/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/platform" element={<Protected><PlatformAdmin /></Protected>} />
            <Route path="/app/platform" element={<Navigate to="/platform" replace />} />
            <Route path="/app/reports" element={<Protected><Reports /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
